import { TeoCore } from '../../TeoCore';
import { AnalysisResult } from '../AnalysisResult';
import type { TeoText } from '../TeoText';
import type { TeoView } from '../TextUi';
import type { GeneratorHistoryEntry } from './GeneratorHistoryEntry';
import { GeneratorStore } from './GeneratorStore';

export type GeneratorUiHandlerStoredState = {
  history: GeneratorHistoryEntry[];
  selectedHistory: number;
};

export type AddAttachedFileResult = { ok: true } | { ok: false; message: string };

export class GeneratorUiHandler {
  public readonly store: GeneratorStore;

  constructor(
    private text: TeoText,
    storedState:
      | GeneratorUiHandlerStoredState
      | { state: GeneratorUiHandlerStoredState }
      | undefined = undefined,
  ) {
    if (storedState) {
      this.store = new GeneratorStore(
        text,
        'state' in storedState ? storedState.state : storedState,
      );
    } else {
      this.store = new GeneratorStore(text);
    }
    this.text.core.dynamicConfig.addEventListener(
      'promptOptionTemplates',
      this.updateTemplates.bind(this),
    );
    this.updateTemplates();
  }

  public changeSelectedHistory(page: number) {
    this.store.selectHistory(page);
  }

  async generateText() {
    // The editor shows the animation while the text is written, the way it does for an analysis.
    const entry = await this.text.store.withLoading(
      () => this.store.generate(),
      'Text generieren...',
    );
    // A generated text goes straight into the optimization, which stops on its own once the
    // text reaches the TPI target.
    if (!entry.generatorError) {
      // Nothing of the generated text is the user's own, so there is no change to point out.
      const runs = await this.text.ai.auto.startLoop({ markChanges: false, maxRuns: 6 });
      // The entry still holds the text as it came out of the generator.
      if (runs > 0) {
        this.store.storeCurrentTextData();
      }
    }
    return entry;
  }

  async createTextTab(options?: { view?: TeoView }) {
    const newText = this.text.core.duplicateText(this.text.id);
    if (!newText) {
      return;
    }
    this.text.core.switchText(newText.id);
    if (options?.view) {
      newText.store.setStateValue('view', options.view);
    }
    newText.isEditable = true;
    newText.events.triggerEvent('textChanged', newText.text);
    const data = newText.getTextData();
    if (data.analysis?.data) {
      newText.events.triggerEvent(
        'gotAnalysis',
        new AnalysisResult(data.analysis.data, newText, data.text),
      );
    }
    // create a history entry
    newText.history?.addHistoryEntry();
    newText.save();
  }

  private updateTemplates() {
    this.store.setStateValue('loading', true);
    const availableTemplates = TeoCore.current.dynamicConfig.options.promptOptionTemplates;
    if (!availableTemplates) {
      return;
    }
    this.store.setStateValue('loading', false);
  }

  toJSON() {
    if (this.text.core.dynamicConfig.options.textGeneratorEnabled) {
      return this.store.toJSON();
    }
    return undefined;
  }
}
